import React, { useEffect, useState } from "react";
import { Button, Empty, Input, Space, Table, Tooltip } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import InsideHeader from "../../components/InsideHeader";
import { fetchWhatsAppDeadLetters } from "../../features/whatsapp/whatsappSlice";
import { formatWhatsAppDate, trimFilters, truncate } from "./whatsappUtils";
import "./whatsapp.css";

const WhatsAppDeadLetter = () => {
  const dispatch = useDispatch();
  const { deadLetterData, deadLetterCount, loadingDeadLetters } = useSelector((state) => state.whatsapp);
  const [filters, setFilters] = useState({});
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const loadDeadLetters = (page = pageIndex, size = pageSize, filterValues = filters) => {
    dispatch(fetchWhatsAppDeadLetters({ start: page, limit: size, ...trimFilters(filterValues) }));
  };

  useEffect(() => {
    dispatch(fetchWhatsAppDeadLetters({ start: 0, limit: 10 }));
  }, [dispatch]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = () => {
    setPageIndex(0);
    loadDeadLetters(0, pageSize, filters);
  };

  const handleClear = () => {
    setFilters({});
    setPageIndex(0);
    loadDeadLetters(0, pageSize, {});
  };

  const columns = [
    {
      title: "Recipient",
      dataIndex: "recipient",
      width: 150,
    },
    {
      title: "Template",
      dataIndex: "templateName",
      width: 190,
      render: (value) => value || "-",
    },
    {
      title: "Retries",
      dataIndex: "retryCount",
      width: 100,
    },
    {
      title: "Error Code",
      dataIndex: "errorCode",
      width: 140,
      render: (value) => value || "-",
    },
    {
      title: "Error Message",
      dataIndex: "errorMessage",
      width: 340,
      render: (value) => (
        <Tooltip title={value}>
          <span>{truncate(value, 120)}</span>
        </Tooltip>
      ),
    },
    {
      title: "Original ID",
      dataIndex: "originalId",
      width: 250,
      render: (value) => (
        <Tooltip title={value}>
          <span>{truncate(value, 36)}</span>
        </Tooltip>
      ),
    },
    {
      title: "Queued At",
      dataIndex: "createdAt",
      width: 180,
      render: formatWhatsAppDate,
    },
    {
      title: "Failed At",
      dataIndex: "failedAt",
      width: 180,
      render: formatWhatsAppDate,
    },
  ];

  return (
    <div className="w-full h-full overflow-y-scroll whatsapp-page">
      <InsideHeader title="WhatsApp Dead Letter" subtitle="Review WhatsApp messages that failed after retries" back={true} />

      <div className="whatsapp-table-shell">
        <div className="whatsapp-toolbar">
          <Space wrap>
            <Input
              placeholder="Recipient"
              value={filters.recipient}
              onChange={(event) => handleFilterChange("recipient", event.target.value)}
              className="whatsapp-filter-control"
            />
            <Input
              placeholder="Template"
              value={filters.templateName}
              onChange={(event) => handleFilterChange("templateName", event.target.value)}
              className="whatsapp-filter-control"
            />
            <Button onClick={handleSearch} type="primary">Search</Button>
            <Button onClick={handleClear}>Clear</Button>
          </Space>

          <Button icon={<ReloadOutlined />} onClick={() => loadDeadLetters()} loading={loadingDeadLetters}>
            Refresh
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={deadLetterData}
          loading={loadingDeadLetters}
          rowKey={(record) => record.id}
          scroll={{ x: 1500 }}
          locale={{ emptyText: <Empty description="No WhatsApp dead-letter messages found" /> }}
          pagination={{
            position: ["bottomCenter"],
            current: pageIndex + 1,
            total: deadLetterCount,
            pageSize,
            showSizeChanger: false,
            hideOnSinglePage: true,
            onChange: (page, size) => {
              setPageIndex(page - 1);
              setPageSize(size);
              loadDeadLetters(page - 1, size, filters);
            },
          }}
        />
      </div>
    </div>
  );
};

export default WhatsAppDeadLetter;
